import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { Button, Icon, Spinner, Text, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import * as Sentry from '@sentry/react-native'
import { productUnitsDictAtom, productsDictAtom } from '../../../global/AtomsProvider'
import { supportApi } from '../../../global/Support/supportApi'
import { catalogueProductNames, draftPriceUnitLabel, isNameInCatalogue } from '../../../global/Support/productDraftPrefill'

const OPEN_STATUSES = new Set(['proposed', 'partially_applied'])

/**
 * ATL-1323 — the support agent's product proposal, rendered under its chat message.
 *
 * The card never saves anything. Each item opens the ordinary product screen in create mode,
 * prefilled, and the customer saves it there after entering the cost price; that is why the button
 * says "open and save" and the hint names the cost price. The card shows an item as saved only once
 * the backend has verified the product in Firestore.
 *
 * `onOpenDraft` is absent where the product screen cannot be reached (the blocked-app screen); the
 * card then lists the proposal without offering to open it.
 */
export default function ProductDraftCard({ actionId, onOpenDraft }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const productsDict = useAtomValue(productsDictAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const catalogueNames = useMemo(() => catalogueProductNames(productsDict), [productsDict])
  const [action, setAction] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [actionError, setActionError] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadFailed(false)
    try {
      setAction(await supportApi.getAction(actionId))
    } catch (error) {
      const status = error?.response?.status
      // 403/404: not this user's to act on (another org member's chat, or a role that cannot create
      // products). The message above still reads; the card is simply not theirs.
      if (status === 403 || status === 404) {
        setHidden(true)
      } else {
        setLoadFailed(true)
        Sentry.captureException(error, { tags: { feature: 'support-product-draft' } })
      }
    } finally {
      setLoading(false)
    }
  }, [actionId])

  useEffect(() => {
    void load()
  }, [load])

  const dismiss = useCallback(async () => {
    setDismissing(true)
    setActionError(false)
    try {
      setAction(await supportApi.dismissAction(actionId))
    } catch (error) {
      const status = error?.response?.status
      if (status === 409) {
        // Already decided or expired elsewhere: show what is true now.
        await load()
      } else {
        setActionError(true)
        Sentry.captureException(error, { tags: { feature: 'support-product-draft' } })
      }
    } finally {
      setDismissing(false)
    }
  }, [actionId, load])

  if (hidden) return null

  const cardStyle = [
    styles.card,
    { backgroundColor: theme['background-basic-color-1'], borderColor: theme['border-basic-color-3'] },
  ]

  if (loading && !action) {
    return (
      <View style={[cardStyle, styles.centered]}>
        <Spinner size="small" />
      </View>
    )
  }

  if (loadFailed || !action) {
    return (
      <View style={cardStyle}>
        <Text category="c1" status="danger">
          {t('support.draftLoadError')}
        </Text>
        <Button size="small" appearance="ghost" onPress={load} style={styles.retry}>
          {t('support.draftRetry')}
        </Button>
      </View>
    )
  }

  const isOpen = OPEN_STATUSES.has(action.status)
  const statusText =
    action.status === 'applied'
      ? t('support.draftAllSaved')
      : action.status === 'dismissed'
        ? t('support.draftDismissed')
        : action.status === 'expired'
          ? t('support.draftExpired')
          : null

  return (
    <View style={cardStyle} testID="support-product-draft-card">
      <Text category="s1" style={styles.title}>
        {t('support.draftTitle')}
      </Text>
      {isOpen ? (
        <Text category="c1" appearance="hint" style={styles.hint}>
          {onOpenDraft ? t('support.draftHint') : t('support.draftOpenInApp')}
        </Text>
      ) : null}

      {action.items.map((item) => {
        const saved = item.applied !== null
        const duplicate = !saved && isNameInCatalogue(catalogueNames, item.name)
        // ATL-1610: a per-kg price without its unit reads as a per-piece price. Only a unit the form
        // will actually select is shown, so the card never names a unit the product will not get.
        const unitLabel = item.salePrice === null ? null : draftPriceUnitLabel(productUnitsDict, item.unitName)
        return (
          <View key={item.index} style={[styles.item, { borderTopColor: theme['border-basic-color-3'] }]}>
            <View style={styles.itemText}>
              <Text category="p2" style={styles.itemName}>
                {item.name}
              </Text>
              <Text category="c1" appearance="hint">
                {item.salePrice === null
                  ? t('support.draftNoPrice')
                  : unitLabel
                    ? t('support.draftSalePricePerUnit', { price: item.salePrice, unit: unitLabel })
                    : t('support.draftSalePrice', { price: item.salePrice })}
              </Text>
              {duplicate ? (
                <Text category="c1" status="warning">
                  {t('support.draftAlreadyInCatalogue')}
                </Text>
              ) : null}
            </View>
            {saved ? (
              <View style={styles.saved}>
                <Icon name="checkmark-circle-2-outline" width={16} height={16} fill={theme['color-success-500']} />
                <Text category="c1" status="success" style={styles.savedText}>
                  {t('support.draftSaved')}
                </Text>
              </View>
            ) : isOpen && onOpenDraft ? (
              <Button
                size="tiny"
                appearance="outline"
                onPress={() => onOpenDraft({ actionId: action.id, item })}
                testID={`support-product-draft-open-${item.index}`}
              >
                {t('support.draftOpen')}
              </Button>
            ) : null}
          </View>
        )
      })}

      {statusText ? (
        <Text category="c1" appearance="hint" style={styles.status}>
          {statusText}
        </Text>
      ) : null}

      {actionError ? (
        <Text category="c1" status="danger" style={styles.status}>
          {t('support.draftActionError')}
        </Text>
      ) : null}

      {isOpen ? (
        <Pressable
          onPress={dismiss}
          disabled={dismissing}
          style={({ pressed }) => [styles.dismiss, (pressed || dismissing) && styles.pressed]}
          accessibilityRole="button"
          testID="support-product-draft-dismiss"
        >
          <Text category="c1" appearance="hint">
            {t('support.draftDismiss')}
          </Text>
        </Pressable>
      ) : null}
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 14,
    marginTop: 2,
    marginBottom: 6,
    maxWidth: 420,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  centered: { alignItems: 'center' },
  title: { fontWeight: '700' },
  hint: { marginTop: 2, marginBottom: 6 },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: 8,
  },
  itemText: { flex: 1 },
  itemName: { fontWeight: '600' },
  saved: { flexDirection: 'row', alignItems: 'center' },
  savedText: { marginLeft: 4 },
  status: { marginTop: 6 },
  retry: { alignSelf: 'flex-start', marginTop: 4 },
  dismiss: { alignSelf: 'center', paddingVertical: 8, paddingHorizontal: 12, marginTop: 2 },
  pressed: { opacity: 0.6 },
})
